// React and related imports
import React, { useState } from 'react';

// i18n hooks
import { useTranslation } from 'react-i18next';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsDarkMode } from '../../redux/selectors/displaySettingsSelectors';

// RTK Query hooks
import {
  useCreateQuestionMutation,
  useUpdateQuestionMutation,
  useFetchAnswersQuery,
} from '../../redux/api/flowApi';

// Interfaces and Enums
import {
  IQuestion,
  IQuestionSubmission,
} from '../../common/interfaces/Question.interface';
import { IAnswer } from '../../common/interfaces/Answer.interface';
import { Status } from '../../common/enums/Status';
import { IMultiLanguageValue } from '../../common/interfaces/MultiLanguageValue.interface';

// Flowbite components
import {
  Badge,
  Button,
  Label,
  Modal,
  TextInput,
  ToggleSwitch,
} from 'flowbite-react';

// Custom components
import { MultiLangQuestionInput } from './MultiLangQuestionInput';
import { AnswersDropdown } from './AnswersDropdown';

// Constants
import { statusIcons } from '../../common/constants/statusIcons';

// Custom Hooks
import { usePreventUnload } from '../../hooks/usePreventUnload';
import { useFormChanges } from '../../hooks/useFormChanges';

interface QuestionModalProps {
  question: IQuestion;
  show: boolean;
  onClose: () => void;
  isModalForAdd: boolean;
}

export const QuestionModal: React.FC<QuestionModalProps> = ({
  question,
  show,
  onClose,
  isModalForAdd,
}) => {
  const { t } = useTranslation();
  const isDarkMode = useAppSelector(selectIsDarkMode);
  const buttonClass = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;
  const [selectedQuestion, setSelectedQuestion] = useState<IQuestion | null>(
    question
  );

  const { hasChanges, hasChangesRef, resetInitialValues } =
    useFormChanges<IQuestion>({
      initialValues: question,
      currentValues: selectedQuestion as IQuestion,
    });

  usePreventUnload({
    shouldPrevent: show && hasChanges,
  });

  const { data: answersData } = useFetchAnswersQuery();

  const answersResponse = answersData?.data || [];

  const [createQuestion, { isLoading: creating }] = useCreateQuestionMutation();
  const [updateQuestion, { isLoading: updating }] = useUpdateQuestionMutation();

  const updateQuestionField = (
    field: keyof IQuestion,
    value: Status | IMultiLanguageValue[] | IAnswer[]
  ) => {
    if (selectedQuestion) {
      setSelectedQuestion({
        ...selectedQuestion,
        [field]: value,
      });
    }
  };

  const onCloseModal = () => {
    if (hasChangesRef.current) {
      const shouldClose = window.confirm(
        t('content.unsavedChangesCloseConfirm')
      );
      if (!shouldClose) return; // If user cancels, don't close the modal
    }

    onClose();
    setSelectedQuestion(null);
  };

  const handleSubmission = async (question: IQuestion) => {
    const processedQuestion: IQuestionSubmission = {
      ...question,
      answers: (question.answers || [])
        .filter((ans) => ans.id)
        .map((ans) => ans.id as string),
    };

    try {
      if (isModalForAdd) {
        await createQuestion(processedQuestion).unwrap();
      } else {
        await updateQuestion({
          updatedQuestion: { ...processedQuestion, id: question?.id },
        }).unwrap();
      }
      setSelectedQuestion(null);
      resetInitialValues(onCloseModal);
    } catch (error) {
      // The toast middleware reports the failure; the modal stays open with the input.
    }
  };

  return (
    <Modal dismissible show={show} onClose={onCloseModal}>
      <Modal.Header className="modal-header">
        <div className="flex items-center">
          <span className="me-3">
            {isModalForAdd
              ? t('questions.addQuestion')
              : t('questions.editQuestion')}
          </span>
          <Badge
            size="sm"
            className="cursor-default bg-gray-200 text-gray-800 group-hover:bg-gray-200 dark:bg-gray-200 dark:text-gray-800 dark:group-hover:bg-gray-200"
          >
            {t(`content.${selectedQuestion?.type}`)}

            {/* Status Indicator */}
            <span
              className={`ms-2 inline-block h-2 w-2 rounded-full ${statusIcons[selectedQuestion?.status || Status.INACTIVE]}`}
            ></span>
          </Badge>
        </div>
      </Modal.Header>
      <Modal.Body className="space-y-5">
        <MultiLangQuestionInput
          t={t}
          question={selectedQuestion as IQuestion}
          updateQuestionField={updateQuestionField}
        />

        <div className="flex items-center space-x-2">
          {/* Disabled Input Field for Answers Options */}
          <TextInput
            type="text"
            value={
              // TODO: Remove optional chaining operator after fixing the API response
              selectedQuestion?.answers?.[0].dataSources?.[0].options
                .map((opt) => opt.value[0].value)
                .join(' - ') || ''
            }
            disabled
            placeholder={t('content.noAnswerSelected')}
            className="w-3/5 p-2"
          />

          <AnswersDropdown
            answers={answersResponse}
            // TODO: Remove optional chaining operator after fixing the API response
            selectedQuestionAnswer={selectedQuestion?.answers?.[0] as IAnswer}
            setSelectedAnswer={(answer) =>
              updateQuestionField('answers', [answer])
            }
          />
        </div>

        {/* Status toggle */}
        <div className="ms-2">
          <div className="mb-2 block">
            <Label htmlFor="status" value={t('content.status')} />
          </div>
          <ToggleSwitch
            id="status"
            checked={selectedQuestion?.status === Status.ACTIVE}
            label={t(`status.${selectedQuestion?.status || Status.INACTIVE}`)}
            onChange={() =>
              updateQuestionField(
                'status',
                selectedQuestion?.status === Status.ACTIVE
                  ? Status.INACTIVE
                  : Status.ACTIVE
              )
            }
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          className={`${buttonClass} me-4`}
          onClick={onCloseModal}
        >
          {t('content.cancel')}
        </Button>
        <Button
          type="button"
          className={buttonClass}
          onClick={() => handleSubmission(selectedQuestion as IQuestion)}
          disabled={!hasChanges || creating || updating}
        >
          {t('content.submit')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
